// External libraries
import * as React from "react";
import { inject } from "mobx-react";

// Components
import EditorialBase from "components/flexComponents/Content/factory/EditorialBase";

// Types
import { ExtendedContextStore } from "typings/flexFramework/FlexDefinitions";
import { EditorialModel, Media, EditorialFlexModule } from "typings/microserviceModels/content-flex-module";
import { ContentFlexComponent, ContentProps } from "components/flexComponents/Content/typings";
import { AspectRatioEnum, ThumborStore } from "stores/thumbor/ThumborStore";
import { LazyLoadedImage } from "src/components/utility/LazyLoadedImage/LazyLoadedImage";

export const imageRatio = (ratio: string) => {
  switch (ratio) {
    case "1-1": {
      return AspectRatioEnum.R_1_1;
    }
    case "3-2": {
      return AspectRatioEnum.R_3_2;
    }
    case "4-3": {
      return AspectRatioEnum.R_4_3;
    }
    case "21-9": {
      return AspectRatioEnum.R_21_9;
    }
    case "5-1": {
      return AspectRatioEnum.R_5_1;
    }
    default: {
      return AspectRatioEnum.DEFAULT;
    }
  }
};

export const safelyResizeImage = (
  mediaUrl: string,
  editorial: EditorialFlexModule,
  context: ExtendedContextStore,
  thumbor?: ThumborStore
): string => {
  if (!thumbor) {
    return mediaUrl;
  }

  const isMobile = context && context.deviceInformation && context.deviceInformation.mobile;

  const width = isMobile ? 360 : 1040;
  const quality = isMobile ? 80 : 100;
  const ratio = imageRatio(editorial.imageAspectRatio);

  return thumbor.safelyGenerateThumborizedImageUrl(mediaUrl, width, undefined, context, ratio, true, quality);
};

interface EditorialMediaImageProps {
  media: Media;
  editorial: EditorialFlexModule;
  context: ExtendedContextStore;
  thumbor?: ThumborStore;
}

export const EditorialMediaImage: React.FC<EditorialMediaImageProps> = inject(
  "thumbor"
)(({ media, editorial, context, thumbor }: EditorialMediaImageProps) => (
  <LazyLoadedImage
    key={media.mediaUrl}
    alt={media.mediaAlt}
    src={safelyResizeImage(media.mediaUrl, editorial, context, thumbor)}
    className="editorialImage"
  />
));

export class ImageEditorial extends EditorialBase {
  /* istanbul ignore next */
  public constructor(editorial: ContentFlexComponent) {
    super(editorial);
  }

  /**
   * An Editorial consists of HTML strings that we parse through with the best of
   * our abilities and display as is on the page. Mainly, we do not control the
   * content of an Editorial, we simply put them in the page.
   */
  public generate(props: ContentProps) {
    return (
      <div id={this.flexComponent.id} data-fm={this.flexComponent.fmId} data-fm-title-id={this.flexComponent.fmTitleId}>
        {this.editorial.items
          .filter((item: EditorialModel) => item.media && item.media.filter((media) => media.mediaUrl))
          .map((item: EditorialModel) => item.media)
          .reduce((item, acum) => acum && acum.concat.apply(acum, item), [])
          .map((media: Media) => {
            return (
              <EditorialMediaImage
                key={media.mediaUrl}
                media={media}
                editorial={this.editorial}
                context={props.context}
              />
            );
          })}
      </div>
    );
  }
}

export default ImageEditorial;
